import { useEffect, useRef, useState } from 'react';
import { Box, Button, Input, Text } from '@chakra-ui/react';

import { useApi } from '@app/hooks';
import { FilterInput } from '@app/components/common/editable-grid/filter-input';
import { GridLookup } from '@app/components/common/editable-grid/model';

export const decodeCollection = (value: unknown) => {
  try {
    const parsed = JSON.parse(String(value));
    return parsed && 'before' in parsed && 'after' in parsed
      ? parsed
      : undefined;
  } catch {
    return undefined;
  }
};
export const photoValues = (row: any) =>
  (row.images || []).map((image) => ({
    image_pid: image.image_pid,
    filename: '',
    content: '',
  }));
export async function loadRouteIds(api: any, id: number): Promise<number[]> {
  const ids: number[] = [];
  let offset = 0;
  let hasMore = true;
  while (hasMore) {
    const result = await api.acb.getOutletRoutes(id, {
      limit: 100,
      offset,
      sort: '+route_number_id',
      filter: { is_active: 'true' },
    });
    if (result?.code !== 200 || !Array.isArray(result.data?.data)) {
      throw new Error('Не удалось загрузить маршруты');
    }
    const items = result.data.data || [];
    ids.push(...items.map((item) => item.route_number_id));
    if (items.length < 100) {
      return [...new Set(ids)].sort((a, b) => a - b);
    }
    offset += items.length;
    hasMore = items.length === 100;
  }
  return [...new Set(ids)].sort((a, b) => a - b);
}

type Props = {
  id: number;
  kind: 'photos' | 'route_numbers' | 'parent_id';
  value: string;
  lookup?: GridLookup;
  onSave: (value: string) => void;
  onCancel: () => void;
};
export function CollectionEditor({
  id,
  kind,
  value,
  lookup,
  onSave,
  onCancel,
}: Props) {
  const api = useApi();
  const apiRef = useRef(api);
  apiRef.current = api;
  const [state, setState] = useState<{ before: any; after: any }>();
  const [labels, setLabels] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [reading, setReading] = useState(false);
  useEffect(() => {
    let live = true;
    const previous = decodeCollection(value);
    const load = async () => {
      if (previous) {
        return previous;
      }
      let before;
      if (kind === 'route_numbers') {
        before = await loadRouteIds(apiRef.current, id);
      } else {
        const result = await apiRef.current.acb.getMasterOutlet(id);
        if (result?.code !== 200 || !result.data) {
          throw new Error('Не удалось загрузить данные');
        }
        before =
          kind === 'photos'
            ? photoValues(result.data)
            : result.data.parent_id ?? null;
      }
      return { before, after: before };
    };
    load()
      .then((data) => {
        if (live) {
          setState(data);
          if (kind === 'route_numbers') {
            Promise.all(
              data.after.map(async (routeId) => {
                try {
                  const result = await apiRef.current.acb.getRoute(routeId);
                  return [
                    String(routeId),
                    result?.data?.name || String(routeId),
                  ];
                } catch {
                  return [String(routeId), String(routeId)];
                }
              })
            ).then((entries) => {
              if (live) {setLabels(Object.fromEntries(entries));}
            });
          }
        }
      })
      .catch((e) => {
        if (live) {
          setError(e.message);
        }
      });
    return () => {
      live = false;
    };
  }, [id, kind, value]);
  return (
    <Box
      minW="280px"
      p={2}
      onKeyDown={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      onPaste={(e) => e.stopPropagation()}
    >
      {error && (
        <Text role="alert" color="red.600" whiteSpace="normal">
          {error}
        </Text>
      )}
      {!state && !error && <Text>Загрузка…</Text>}
      {state && kind === 'route_numbers' && (
        <FilterInput
          filter={{ id: kind, label: 'Маршруты', filterType: 'ids', lookup }}
          value={state.after.join(',')}
          labels={labels}
          onChange={(v) =>
            setState({
              ...state,
              after: v
                ? v
                    .split(',')
                    .map(Number)
                    .sort((a, b) => a - b)
                : [],
            })
          }
        />
      )}
      {state && kind === 'parent_id' && (
        <Input
          aria-label="ID ТТ-предшественника"
          type="number"
          value={state.after ?? ''}
          onChange={(e) =>
            setState({
              ...state,
              after: e.target.value === '' ? null : Number(e.target.value),
            })
          }
        />
      )}
      {state && kind === 'photos' && (
        <>
          {state.after.map((photo, index) => (
            <Box key={index}>
              <Text as="span">
                {photo.filename || `Фото ${photo.image_pid}`}
              </Text>
              <Button
                size="xs"
                ml={2}
                disabled={reading}
                onClick={() =>
                  setState({
                    ...state,
                    after: state.after.filter((_, i) => i !== index),
                  })
                }
              >
                Убрать
              </Button>
            </Box>
          ))}
          <Input
            type="file"
            aria-label="Добавить фотографии"
            accept="image/*"
            multiple
            disabled={reading}
            onChange={async (e) => {
              const files = Array.from(e.target.files || []);
              e.target.value = '';
              setReading(true);
              setError('');
              try {
                const photos = await Promise.all(
                  files.map(
                    (file) =>
                      new Promise((resolve, reject) => {
                        if (!file.type.startsWith('image/')) {
                          reject(new Error('Выберите изображения'));
                          return;
                        }
                        const reader = new FileReader();
                        reader.onload = () =>
                          resolve({
                            filename: file.name,
                            content: String(reader.result).split(',')[1],
                          });
                        reader.onerror = () =>
                          reject(new Error('Не удалось прочитать файл'));
                        reader.readAsDataURL(file);
                      })
                  )
                );
                setState((current) => ({
                  ...current,
                  after: [...current.after, ...photos],
                }));
              } catch (e) {
                setError(e.message);
              } finally {
                setReading(false);
              }
            }}
          />
        </>
      )}
      <Button
        size="xs"
        mt={2}
        disabled={!state || reading}
        onClick={() => {
          if (
            kind === 'parent_id' &&
            state.after != null &&
            (!Number.isSafeInteger(state.after) || state.after === id)
          ) {
            setError('Укажите целый ID другой торговой точки');
            return;
          }
          onSave(JSON.stringify(state));
        }}
      >
        В черновик
      </Button>
      <Button size="xs" mt={2} ml={2} disabled={reading} onClick={onCancel}>
        Отмена
      </Button>
    </Box>
  );
}
